import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { format } from 'date-fns';
import api from '../../api';
import ApprovalModal from './ApprovalModal';
import EscalationModal from './EscalationModal';
import TaskCountTag from '../misc/TaskCountTag';
import './task-list.css';
import FakePic from '../feed/items/FakePic';
import { ReactComponent as HornIcon } from '../../icons/horn.svg';
import { ReactComponent as ThumbsUpIcon } from '../../icons/thumbs-up.svg';
import { ReactComponent as DownCaretIcon } from '../../icons/little-down-caret.svg';
import { ReactComponent as RightCaretIcon } from '../../icons/right-caret.svg';
import { ReactComponent as CheckCircleIcon } from '../../icons/check-circle.svg';
import { Button, Spinner } from 'react-bootstrap';

function TaskListPage() {
  const { campaignId } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [campaignStatuses, setCampaignStatuses] = useState([]);
  const [campaigns, setCampaigns] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isApproval, setIsApproval] = useState(false);
  const [view, setView] = useState('allActions');
  const [expandedSections, setExpandedSections] = useState({});
  const [taskLadder, setTaskLadder] = useState(null);
  const [selected, setSelected] = useState([]);

  // Ensures we don't show loading states for the reloads that happen by interval
  const hasShownLoadingRef = useRef(false);

  useEffect(() => {
    // We want to reset this when we get a new campaign
    hasShownLoadingRef.current = false;
  }, [campaignId]);

  const fetchTasks = async () => {
    if (!hasShownLoadingRef.current) {
      setLoading(true);
    }

    try {
      const token = await getAccessTokenSilently();
      const [approvals, escalations] = await Promise.all([
        api.get('/approvals', {
          params: { campaign_id: campaignId },
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get('/escalations', {
          params: { campaign_id: campaignId },
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const processedApprovals = approvals.data.map((item) => ({
        ...item,
        type: 'approval',
        date: new Date(item.scheduled_time),
      }));

      const processedEscalations = escalations.data.map((item) => ({
        ...item,
        type: 'escalation',
        date: new Date(item.created_at),
      }));

      const mergedTasks = [...processedApprovals, ...processedEscalations].sort(
        (a, b) => a.date - b.date,
      );
      setTasks(mergedTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
      hasShownLoadingRef.current = true;
    }
  };

  const fetchCampaignStatuses = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get('/actions-required', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCampaignStatuses(response.data);
    } catch (error) {
      console.error('Error fetching campaign statuses:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get('/oagents', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const campaignMap = response.data.reduce((acc, campaign) => {
        acc[campaign.id] = campaign;
        return acc;
      }, {});
      setCampaigns(campaignMap);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchCampaigns();
    fetchCampaignStatuses();

    const intervalId = setInterval(() => {
      fetchTasks();
      fetchCampaignStatuses();
    }, 60000); // Reload tasks and statuses every 60 seconds

    return () => clearInterval(intervalId); // Clear the interval when the component is unmounted
  }, [campaignId]);

  const batchApprove = async () => {
    let reload;

    try {
      // Optimistically update things
      setTasks((prev) => {
        reload = prev.filter((x) => selected.includes(x.id));
        return prev.filter((x) => !selected.includes(x.id));
      });

      const token = await getAccessTokenSilently();
      await api.post(
        '/approvals/batch',
        {
          action: 'approve',
          approvalIds: selected,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setSelected([]);
    } catch (error) {
      console.error('Error doing a batch approval:', error);
      setTasks((prev) => {
        return [...prev, ...reload];
      });
      alert(
        'An error occured approving your tasks, please refresh and try again',
      );
    }
  };

  useEffect(() => {
    if (campaignId) {
      setExpandedSections({
        ...expandedSections,
        [campaignId]: true,
      });
    } else {
      const allSections = tasks.reduce((acc, task) => {
        acc[task.campaign_id] = true;
        return acc;
      }, {});
      setExpandedSections(allSections);
    }
  }, [tasks, campaignId]);

  const openModal = (task, next) => {
    setSelectedTask(task);
    setIsApproval(task.type === 'approval');
    setTaskLadder(tasks.slice(next));
  };

  const closeModal = () => {
    setSelectedTask(null);
    fetchTasks(); // Refresh the task list
    fetchCampaignStatuses(); // Refresh the campaign statuses
  };

  const toggleSection = (campaignId) => {
    setExpandedSections((prevState) => ({
      ...prevState,
      [campaignId]: !prevState[campaignId],
    }));
  };

  const chooseNext = () => {
    const hasNextTask = taskLadder.length > 1;
    if (hasNextTask) {
      setTaskLadder((prev) => {
        setSelectedTask(taskLadder[1]);
        return prev.slice(1);
      });

      return true;
    } else {
      closeModal();
      fetchTasks();
      return false;
    }
  };

  const toggleSelected = (itemIds, selected) => {
    setSelected((prev) => {
      if (selected) {
        return [...prev, ...itemIds];
      }
      return prev.filter((x) => !itemIds.includes(x));
    });
  };

  const renderTaskItem = (item) => {
    const isSelected = selected.find((x) => x === item.id);
    return (
      <div className='task-item-outer-wrapper-page' key={item.id}>
        <div className='task-item'>
          <FakePic
            url={item.known_org_logo_url}
            onClick={() => openModal(item)}
          />
          <div className='left-side'>
            <div className='clickable' onClick={() => openModal(item)}>
              <p>
                {item.is_response
                  ? `Reply to ${item.company}`
                  : `Outreach to ${item.company}`}
                <small> • {item.prospects[0].name || ''}</small>
              </p>
              <small>{format(item.date, 'PPpp')}</small>
            </div>
            <div className='view-and-selector'>
              <span onClick={() => openModal(item)}>View approval</span>
              <div className='spacer-sorry-matt'></div>
              <div className='selector'>
                <input
                  type='checkbox'
                  className='sexy-checkbox'
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleSelected([item.id], !isSelected);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCampaignItem = (campaign) => (
    <div className='task-item-outer-wrapper-page' key={campaign.id}>
      <div
        className='task-item campaign-action'
        onClick={() => navigate(`/campaign/${campaign.id}`)}
      >
        <div className='horn-holder'>
          <HornIcon />
        </div>
        <div className='left-side'>
          <p>
            {campaign.action_state === 'IMPORTING' && (
              <>
                <b>{campaign.name}</b> is importing prospects
              </>
            )}
            {campaign.action_state === 'DRAFT' && (
              <>
                Complete details for <b>{campaign.name}</b>
              </>
            )}
            {campaign.action_state === 'GENERATE_EMAIL' && (
              <>
                Generate your first email for <b>{campaign.name}</b>
              </>
            )}
            {campaign.action_state === 'AUTOPILOT_SWITCH_ON' && (
              <>
                Start campaign <b>{campaign.name}</b>
              </>
            )}
          </p>
          <span className='view-span'>Go to Campaign</span>
        </div>
      </div>
    </div>
  );

  // Helper function to group tasks by their campaign_id
  const groupTasksByCampaignId = useCallback((tasks) => {
    return tasks.reduce((acc, task) => {
      if (!acc[task.campaign_id]) {
        acc[task.campaign_id] = [];
      }
      acc[task.campaign_id].push(task);
      return acc;
    }, {});
  }, []);

  const filteredTasks = useCallback(() => {
    if (view === 'emailApprovals') {
      return tasks
        .filter((task) => task.type === 'approval')
        .sort((a, b) => {
          if (a.is_response === b.is_response) {
            return 0;
          }
          if (a.is_response) {
            return -1;
          }
          return 1;
        });
    } else if (view === 'escalations') {
      return tasks.filter((task) => task.type === 'escalation');
    } else if (view === 'allActions') {
      return tasks;
    } else {
      return [];
    }
  }, [view, tasks]);

  const getViewContent = () => {
    const campaignTasks = groupTasksByCampaignId(filteredTasks());
    const campaignContent = campaignStatuses
      .filter((campaign) => campaign.action_state !== 'NO_ACTION')
      .map(renderCampaignItem);

    const allInCampaignSelected = campaignTasks[campaignId]
      ? campaignTasks[campaignId].filter((x) => selected.includes(x.id))
          .length === campaignTasks[campaignId].length
      : true;
    const someInCampaignSelected = campaignTasks[campaignId]
      ? campaignTasks[campaignId].some((x) => selected.includes(x.id))
      : true;

    return (
      <>
        {Object.keys(campaignTasks).map((campaignId) => (
          <div key={campaignId}>
            <div
              className={`expandable-section-header ${!expandedSections[campaignId] && 'collapsed'}`}
            >
              <div
                className='expander-and-text'
                onClick={() => toggleSection(campaignId)}
              >
                {expandedSections[campaignId] ? (
                  <DownCaretIcon />
                ) : (
                  <RightCaretIcon />
                )}
                <div className='tag-and-text'>
                  {campaigns[campaignId]
                    ? `Campaign: ${campaigns[campaignId].name}`
                    : `Campaign ${campaignId}`}
                  <TaskCountTag campaignId={campaignId} />
                </div>
              </div>
              <div className='selection-options'>
                {someInCampaignSelected && (
                  <Button className='approve-selected' onClick={batchApprove}>
                    <CheckCircleIcon />
                    Approve selected
                  </Button>
                )}
                <Button
                  onClick={() =>
                    toggleSelected(
                      campaignTasks[campaignId].map((x) => x.id),
                      !allInCampaignSelected,
                    )
                  }
                >
                  {allInCampaignSelected ? 'Deselect all' : 'Select all'}
                </Button>
              </div>
            </div>
            {expandedSections[campaignId] &&
              campaignTasks[campaignId].map(renderTaskItem)}
          </div>
        ))}
        {(view === 'allActions' || view === 'campaigns') &&
          !campaignId &&
          campaignContent.length > 0 && (
            <div>
              <h6
                className={`expandable-section-header ${!expandedSections['campaignRequirements'] && 'collapsed'}`}
                onClick={() => toggleSection('campaignRequirements')}
              >
                Campaign Requirements{' '}
                {expandedSections['campaignRequirements'] ? (
                  <DownCaretIcon />
                ) : (
                  <RightCaretIcon />
                )}
              </h6>
              {expandedSections['campaignRequirements'] && campaignContent}
            </div>
          )}
      </>
    );
  };

  const getQuantity = useCallback(() => {
    return (
      tasks.length +
      (campaignId
        ? 0
        : campaignStatuses.filter(
            (campaign) => campaign.action_state !== 'NO_ACTION',
          ).length)
    );
  }, [tasks, view, campaigns]);

  const shouldShowEmpty =
    getQuantity() === 0 ||
    (['escalations', 'emailApprovals'].includes(view) &&
      filteredTasks().length === 0) ||
    (view === 'campaigns' &&
      campaignStatuses.filter(
        (campaign) => campaign.action_state !== 'NO_ACTION',
      ).length === 0);

  return (
    <div className='page-wrapper'>
      <div className='page-wrapper-inner task-list-page'>
        <div className='page-header-area'>
          <div className='title-and-text'>
            <h3>Actions</h3>
          </div>
        </div>
        <div className={`task-list-page-lower ${loading ? 'loading' : ''}`}>
          <div className='left-panel'>
            <div className='actions-menu'>
              <div
                className={view === 'allActions' ? 'selected' : ''}
                onClick={() => setView('allActions')}
              >
                <span>All actions</span>
                <span>{getQuantity()}</span>
              </div>
              <div
                className={view === 'emailApprovals' ? 'selected' : ''}
                onClick={() => setView('emailApprovals')}
              >
                <span>Email approvals</span>
                <span>
                  {tasks.filter((task) => task.type === 'approval').length}
                </span>
              </div>
              <div
                className={view === 'escalations' ? 'selected' : ''}
                onClick={() => setView('escalations')}
              >
                <span>Escalations</span>
                <span>
                  {tasks.filter((task) => task.type === 'escalation').length}
                </span>
              </div>
              {!campaignId && (
                <div
                  className={view === 'campaigns' ? 'selected' : ''}
                  onClick={() => setView('campaigns')}
                >
                  <span>Campaigns</span>
                  <span>
                    {
                      campaignStatuses.filter(
                        (campaign) => campaign.action_state !== 'NO_ACTION',
                      ).length
                    }
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className='right-panel'>
            {loading && (
              <div className='fake-items'>
                <div className='spinner-abs'>
                  <Spinner />
                </div>
                <div className='fake-item'>
                  <div className='fake-header'></div>
                  <div>
                    <div className='fake-profile-pic'></div>
                  </div>
                </div>
                <div className='fake-item'>
                  <div className='fake-header'></div>
                  <div>
                    <div className='fake-profile-pic'></div>
                  </div>
                </div>
                <div className='fake-item'>
                  <div className='fake-header'></div>
                  <div>
                    <div className='fake-profile-pic'></div>
                  </div>
                </div>
                <div className='fake-item'>
                  <div className='fake-header'></div>
                  <div>
                    <div className='fake-profile-pic'></div>
                  </div>
                </div>
              </div>
            )}
            {!loading && !shouldShowEmpty && (
              <div className='right-panel-inner'>{getViewContent()}</div>
            )}
            {!loading && shouldShowEmpty && (
              <div className='empty-state'>
                <ThumbsUpIcon />
                <div>
                  <h6>You don't have any actions to deal with</h6>
                  <p>Well done. You've caught up with all your actions.</p>
                </div>
              </div>
            )}
          </div>
          {isApproval ? (
            <ApprovalModal
              show={!!selectedTask}
              onHide={closeModal}
              task={selectedTask}
              chooseNext={chooseNext}
            />
          ) : (
            <EscalationModal
              show={!!selectedTask}
              onHide={closeModal}
              task={selectedTask}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default TaskListPage;
